import { useState, useCallback, useRef, useEffect } from "react";
import { Link } from "wouter";
import { Lock } from "lucide-react";
import { mergePdfs, loadPdfSafely } from "./operations";
import { planFilePages, hasPageEdits, arrangementFromPlans, type PageEdits, type ArrangedPage } from "./mergePagePlan";
import { MergePageArranger } from "./MergePageArranger";
import { MERGE_IMAGE_ACCEPT, isMergeImage, imageToPdfFile } from "./mergeImages";
import { MergePagePicker } from "./MergePagePicker";
import { renderFirstPageThumbnail } from "./pdfThumbnails";
import { PdfPageStrip } from "./PdfPageStrip";
import { formatSelectionSummary, formatMergedSummary } from "./mergeSummary";
import { pdfBytesToBlob } from "./utils";
import { unlockPdfIfNeeded } from "./unlockPdf";
import { requestPdfPassword } from "@/hooks/usePdfPasswordPrompt";
import { duplicateIndexes } from "@/components/reorderMath";
import { FileDropZone, ProcessingState, DownloadButton } from "@/components/ToolLayout";
import { StickyActionBar, STICKY_BAR_PRIMARY_CLASS } from "@/components/StickyActionBar";
import { ToolWithSidebar } from "@/components/ToolWithSidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ReorderableFileList, findDuplicateFiles } from "@/components/ReorderableFileList";
import { trackToolCompleted } from "@/lib/analytics";

// What the UI needs to know about each file after it's been checked.
// loadPdfSafely also returns the parsed pdf-lib document, which used to
// be kept in state for every file added -- all of that memory for
// nothing, since only the status and page count are ever read.
const isPdfFile = (f: File) => f.type === "application/pdf" || /\.pdf$/i.test(f.name);

type FileInfo = { status: "ok"; pageCount: number; hasFormFields: boolean } | { status: "encrypted" } | { status: "invalid" };

const THUMBNAIL_MAX_BYTES = 20 * 1024 * 1024;

// Stable per-file identity that survives reordering -- array index
// can't be used as the key here since ReorderableFileList's onReorder
// changes which index points at which file.
function fileKey(file: File): string {
  return `${file.name}-${file.size}-${file.lastModified}`;
}

const TOOL_INFO_CONTENT = {
  // Combined "what" and "how" into one panel -- the reference mockup's
  // sidebar only has two panels (what it does, pro tips), not three, and
  // splitting "how to use it" into its own panel would need a third slot
  // this layout doesn't have. Rather than drop real, useful instructions
  // to fit the mockup exactly, folding them together keeps all the same
  // information the full-width version had.
  what: `A tool for combining two or more PDF files into a single document, in whatever order you choose. This is the everyday fix for the common situation where you've scanned several pages separately, or received a contract as multiple attachments, and need them as one file to send or print.\n\nDrop your PDF files into the upload area (or select them one at a time), then drag to reorder them into the sequence you want — or use the up/down arrow buttons on each file if you'd rather not drag. Once the order looks right, click merge and download the combined file. Need only some pages? Type a range such as 1-3,5 next to a file, or open Choose pages to see small page previews, leave pages out and rotate them. To mix pages from different files, open Arrange all pages: every page of the result shows as a preview that you can drag into any order, rotate or delete. JPG and PNG pictures can be added too; each becomes one page. The merge happens entirely in your browser, so nothing is uploaded to a server in the process.`,
  tip: `If you're merging a scanned document with a typed cover letter, merge them in the order a reader would expect to see them — cover letter first, then the scanned pages. Adding the same file twice by accident shows a warning, but doesn't block you — remove the duplicate from the list if it wasn't intended.`,
};

export default function MergePdf() {
  const [files, setFiles] = useState<File[]>([]);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<Blob | null>(null);
  // Per-file validation status, keyed by fileKey() -- populated
  // asynchronously as each newly-added file is checked. Absent from
  // this map means "still checking" (handled as a neutral/pending
  // state in the UI below), not "assumed ok".
  const [fileStatuses, setFileStatuses] = useState<Map<string, FileInfo>>(new Map());
  // First-page preview per file, same stable key as fileStatuses.
  const [thumbnails, setThumbnails] = useState<Map<string, string>>(new Map());
  // Optional page range per file ("1-3,5"), keyed by the File itself so it follows the row when rows are reordered.
  const [ranges, setRanges] = useState<Map<File, string>>(new Map());
  // Pages turned off / rotated per file in the optional "Choose pages" panel, and which file's panel is open.
  const [edits, setEdits] = useState<Map<File, PageEdits>>(new Map());
  const [pickerFile, setPickerFile] = useState<File | null>(null);
  const [padOdd, setPadOdd] = useState(false);
  // Pictures added to the list, each already made into a one-page PDF (shown with a tag).
  const [fromImage, setFromImage] = useState<Set<File>>(new Set());
  // The page-by-page order chosen in "Arrange all pages" (null = follow the file list, ranges and Choose pages). File indexes refer to the valid files.
  const [arranged, setArranged] = useState<ArrangedPage[] | null>(null);
  const [arrangerOpen, setArrangerOpen] = useState(false);
  const arrangedRef = useRef<ArrangedPage[] | null>(null);
  arrangedRef.current = arranged;
  const cancelledRef = useRef(false);

  // The arranged page order is built from the current files, ranges and page choices, so any change to them drops it
  // (the arranger starts again from the new state) rather than silently merging stale pages.
  useEffect(() => {
    if (arrangedRef.current) toast.info("The page arrangement was reset because the files or page choices changed.");
    setArranged(null);
    setArrangerOpen(false);
  }, [files, fileStatuses, ranges, edits, padOdd]);

  // Validate one file in the background -- not blocking it appearing in the list, since that's
  // still useful feedback (reorder, remove) even before validation resolves. Confirmed as a real,
  // reproducible bug before this existed: adding a password-protected or damaged PDF alongside good
  // ones gave no indication anything was wrong until Merge was clicked and the whole operation
  // failed with one generic, file-unattributed error.
  const validateFile = useCallback((file: File) => {
    const key = fileKey(file);
    loadPdfSafely(file).then(status => {
      const info: FileInfo = status.status === "ok" ? { status: "ok", pageCount: status.pageCount, hasFormFields: status.hasFormFields } : { status: status.status };
      setFileStatuses(prev => new Map(prev).set(key, info));
      // A thumbnail means opening the file a second time in pdf.js. Measured live on a 29 MB,
      // 300-page file: the page count was ready in 1.3 s but the thumbnail took 12 s and a second
      // full parse -- not worth it for a preview of a very large file, so past this size the row
      // keeps its plain icon.
      if (status.status === "ok" && file.size <= THUMBNAIL_MAX_BYTES) {
        renderFirstPageThumbnail(file).then(url => {
          if (url) setThumbnails(prev => new Map(prev).set(key, url));
        });
      }
    });
  }, []);

  const handleFiles = useCallback(async (newFiles: File[]) => {
    if (newFiles.some(f => !isPdfFile(f) && !isMergeImage(f))) toast.error("Only PDF, JPG and PNG files are accepted");

    // Each picture becomes a one-page PDF right here in the browser, then behaves like any other file in the list.
    // The order the person selected is kept.
    const added: File[] = [];
    const madeFromPictures: File[] = [];
    for (const f of newFiles) {
      if (isPdfFile(f)) { added.push(f); continue; }
      if (!isMergeImage(f)) continue;
      try {
        const page = await imageToPdfFile(f);
        madeFromPictures.push(page);
        added.push(page);
      } catch (err) {
        toast.error(`"${f.name}" ${err instanceof Error ? err.message : "could not be read"}`);
      }
    }
    if (madeFromPictures.length > 0) setFromImage(prev => { const next = new Set(prev); madeFromPictures.forEach(x => next.add(x)); return next; });

    setFiles(prev => {
      const dupes = findDuplicateFiles(prev, added);
      if (dupes.length > 0) {
        // Names the file(s): "This file is already in your list" left the person to work out which
        // one (QA MP-16). Repeats inside a single selection are caught too.
        const names = dupes.map(f => `"${f.name}"`);
        const listed = names.length <= 3 ? names.join(", ") : `${names.slice(0, 3).join(", ")} and ${names.length - 3} more`;
        toast.warning(
          dupes.length === 1
            ? `${names[0]} is already in your list — added anyway, remove it below if that wasn't intended.`
            : `${listed} are already in your list — added anyway, remove them below if that wasn't intended.`,
        );
      }
      return [...prev, ...added];
    });
    setResult(null);
    added.forEach(validateFile);
  }, [validateFile]);

  // A password-protected file the person skipped stays in the list, locked (FileDropZone's
  // keepSkippedProtectedFiles). "Unlock" asks for the password again and swaps in the decrypted copy
  // -- without having to pick the file from disk a second time (QA MP-10).
  const unlockRow = async (file: File) => {
    const result = await unlockPdfIfNeeded(file, requestPdfPassword);
    if (result.status === "unlocked") {
      const oldKey = fileKey(file);
      setFiles(prev => prev.map(f => (f === file ? result.file : f)));
      setRanges(prev => { const next = new Map(prev); const r = next.get(file); if (r !== undefined) { next.delete(file); next.set(result.file, r); } return next; });
      setEdits(prev => { const next = new Map(prev); const e = next.get(file); if (e) { next.delete(file); next.set(result.file, e); } return next; });
      setPickerFile(prev => (prev === file ? null : prev));
      setFileStatuses(prev => { const next = new Map(prev); next.delete(oldKey); return next; });
      setThumbnails(prev => { const next = new Map(prev); next.delete(oldKey); return next; });
      validateFile(result.file);
      setResult(null);
      toast.success(`Unlocked "${file.name}"`);
    } else if (result.status === "failed") {
      toast.error(`Couldn't open "${file.name}" — its protection isn't supported here`);
    }
    // cancelled: it simply stays locked in the list
  };

  const removeFile = (index: number) => {
    setFiles(prev => {
      const removed = prev[index];
      if (removed) {
        setEdits(m => { const next = new Map(m); next.delete(removed); return next; });
        setPickerFile(p => (p === removed ? null : p));
        const removedKey = fileKey(removed);
        setFileStatuses(statuses => {
          const next = new Map(statuses);
          next.delete(removedKey);
          return next;
        });
        setThumbnails(prev => {
          const next = new Map(prev);
          next.delete(removedKey);
          return next;
        });
      }
      return prev.filter((_, i) => i !== index);
    });
    setResult(null);
  };

  const goodFiles = files.filter(f => fileStatuses.get(fileKey(f))?.status === "ok");
  // What a typed range selects from one file: its page count when blank, the selected count when valid, or an error message.
  const selection = (f: File): { count: number; error?: string } => {
    const info = fileStatuses.get(fileKey(f));
    if (info?.status !== "ok") return { count: 0 };
    const r = planFilePages(info.pageCount, ranges.get(f), edits.get(f));
    return "error" in r ? { count: 0, error: r.error } : { count: r.pages.length };
  };
  const goodPageCount = arranged ? arranged.length : goodFiles.reduce((sum, f) => sum + selection(f).count, 0);
  const rangeErrors = goodFiles.filter(f => selection(f).error).length;
  const pendingCount = files.filter(f => !fileStatuses.has(fileKey(f))).length;
  const badFileCount = files.filter(f => {
    const s = fileStatuses.get(fileKey(f))?.status;
    return s === "encrypted" || s === "invalid";
  }).length;

  const handleStop = () => {
    cancelledRef.current = true;
    setProcessing(false);
    setProgress(0);
  };

  const handleMerge = async () => {
    // Merges only the files that passed validation -- a password-
    // protected or damaged file no longer takes the whole operation
    // down with it; it's simply excluded, with its own status badge
    // in the list explaining why.
    if (goodFiles.length < 2) { toast.error("Please add at least 2 valid PDF files"); return; }
    if (rangeErrors > 0) { toast.error("Fix the page range marked in red first"); return; }
    if (arranged && arranged.length === 0) { toast.error("Every page was deleted in Arrange all pages. Press Start over there."); return; }
    // No usage check here: merging is free, the use is taken at Download (DownloadButton).
    cancelledRef.current = false;
    setProcessing(true);
    setProgress(0);
    try {
      const bytes = await mergePdfs(goodFiles, (current, total) => setProgress((current / total) * 100), { ranges: goodFiles.map(f => ranges.get(f)), pageEdits: goodFiles.map(f => edits.get(f)), padOdd, ...(arranged ? { arrangement: arranged } : {}) });
      if (cancelledRef.current) return; // stopped while this was in flight -- discard silently
      setResult(pdfBytesToBlob(bytes));
      trackToolCompleted("merge-pdf", "pdf-tools");
      toast.success(
        badFileCount > 0
          ? `Merged ${goodFiles.length} PDFs (skipped ${badFileCount} that couldn't be read)`
          : "PDFs merged successfully!"
      );
    } catch (err) {
      if (cancelledRef.current) return;
      console.error("Merge failed:", err);
      // mergePdfs itself now names the actual file when something
      // still goes wrong despite the up-front validation above (a
      // narrow window: e.g. a file that validated fine but changed on
      // disk before Merge was clicked) -- surface that real message
      // instead of the old flat "Failed to merge PDFs".
      toast.error(err instanceof Error ? err.message : "Failed to merge PDFs");
    }
    finally { setProcessing(false); }
  };

  // Matches the tool's own editorial copy, which already promises this
  // -- "named automatically based on your first file's name" -- a real,
  // confirmed mismatch where the actual download was always the literal
  // string "merged.pdf" regardless of what the first file was called.
  const mergedFileName = goodFiles.length > 0
    ? `${goodFiles[0].name.replace(/\.pdf$/i, "")}-merged.pdf`
    : "merged.pdf";

  // Rows that repeat an earlier row (same name and size) get a small tag; the toast on adding names
  // the file, but the list itself used to look identical (QA MP-16).
  const dupIdx = duplicateIndexes(files);

  const mergeLabel = badFileCount > 0 && goodFiles.length >= 2
    ? `Merge the other ${goodFiles.length} PDFs`
    : goodFiles.length < 2
      ? "Add at least 2 valid PDFs to merge"
      : `Merge ${goodFiles.length} PDFs`;

  // What a row says about its file once checked.
  const statusExtra = (status: FileInfo | undefined, file: File) => {
    if (!status) return <span className="text-xs text-muted-foreground whitespace-nowrap">Checking…</span>;
    if (status.status === "ok") {
      const sel = selection(file);
      return (
        <>
          <span className="text-xs text-muted-foreground whitespace-nowrap">{status.pageCount} {status.pageCount === 1 ? "page" : "pages"}</span>
          <span className="inline-flex flex-col">
            <Input
              value={ranges.get(file) ?? ""}
              onChange={(e) => { const v = e.target.value; setRanges(prev => new Map(prev).set(file, v)); setResult(null); }}
              placeholder="All pages"
              aria-label={`Pages to use from ${file.name}`}
              aria-invalid={!!sel.error}
              className={`h-11 w-28 text-base sm:h-7 sm:text-xs ${sel.error ? "border-red-500" : ""}`}
              data-testid="merge-range"
            />
            {sel.error && <span className="text-xs text-red-600" data-testid="merge-range-error">{sel.error}</span>}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-11 px-3 text-xs sm:h-7 sm:px-2"
            aria-expanded={pickerFile === file}
            onClick={() => setPickerFile(prev => (prev === file ? null : file))}
            data-testid="merge-choose-pages"
          >
            {hasPageEdits(edits.get(file)) ? "Choose pages (edited)" : "Choose pages"}
          </Button>
          {status.hasFormFields && (
            <span className="text-xs text-amber-600 sm:whitespace-nowrap">Has form fields — they won't stay fillable once merged</span>
          )}
        </>
      );
    }
    if (status.status === "encrypted") {
      return (
        <span className="inline-flex items-center gap-1.5 text-xs text-amber-600">
          <Lock className="h-3 w-3 shrink-0" aria-hidden="true" />
          <span>Locked — left out of the merge</span>
          <Button type="button" variant="outline" size="sm" className="h-11 px-3 text-xs sm:h-6 sm:px-2" onClick={() => void unlockRow(file)}>
            Unlock
          </Button>
        </span>
      );
    }
    return (
      <span className="text-xs text-destructive sm:whitespace-nowrap">
        Damaged —{" "}
        <Link href="/tool/repair-pdf" className="underline">try repairing it</Link>
      </span>
    );
  };

  return (
    <ToolWithSidebar toolId="merge-pdf" categoryId="pdf-tools" whatItDoes={TOOL_INFO_CONTENT.what} proTip={TOOL_INFO_CONTENT.tip}>
      <FileDropZone accept={`.pdf,${MERGE_IMAGE_ACCEPT}`} acceptLabel="PDF, JPG or PNG" multiple keepSkippedProtectedFiles onFiles={handleFiles} label="Drop PDF files or pictures here or click to browse" description="Select several PDF files (and JPG or PNG pictures) to merge them into one" gateAtDownload compactOnPhone={files.length > 0} />
      {files.length > 0 && (
        <div className="mt-6 space-y-2">
          <p className="text-sm font-medium text-muted-foreground mb-3">
            {formatSelectionSummary({ fileCount: files.length, pageCount: goodPageCount, pendingCount, unreadableCount: badFileCount })}
          </p>
          <ReorderableFileList
            files={files}
            onReorder={setFiles}
            onRemove={removeFile}
            thumbnailUrls={files.map(f => thumbnails.get(fileKey(f)) ?? null)}
            thumbnailFit="page"
            rowActions={() => null /* phones: full file names and a grid of 44px reorder/remove buttons */}
            renderItemExtra={(index) => {
              const file = files[index];
              const status = fileStatuses.get(fileKey(file));
              const dupTag = dupIdx.has(index)
                ? <span className="text-xs rounded border border-amber-600/50 px-1.5 py-0.5 text-amber-600 whitespace-nowrap" title="The same file appears earlier in the list">Duplicate</span>
                : null;
              const picTag = fromImage.has(file)
                ? <span className="text-xs rounded border border-border px-1.5 py-0.5 text-muted-foreground whitespace-nowrap" title="A picture, placed on a US Letter page (portrait or landscape to suit it)">Picture as page</span>
                : null;
              return <>{picTag}{dupTag}{statusExtra(status, file)}</>;
            }}
          />
          {pickerFile && files.includes(pickerFile) && fileStatuses.get(fileKey(pickerFile))?.status === "ok" && (
            <MergePagePicker
              file={pickerFile}
              pageCount={(fileStatuses.get(fileKey(pickerFile)) as { pageCount: number }).pageCount}
              rangeText={ranges.get(pickerFile) ?? ""}
              edits={edits.get(pickerFile) ?? {}}
              onChange={(next) => { setEdits(prev => new Map(prev).set(pickerFile, next)); setResult(null); }}
              onClose={() => setPickerFile(null)}
            />
          )}
          {goodFiles.length > 0 && rangeErrors === 0 && (
            <div className="mt-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="max-sm:min-h-11"
                aria-expanded={arrangerOpen}
                onClick={() => {
                  if (!arrangerOpen && !arranged) setArranged(arrangementFromPlans(goodFiles.map(f => {
                    const info = fileStatuses.get(fileKey(f)) as { pageCount: number };
                    return planFilePages(info.pageCount, ranges.get(f), edits.get(f));
                  })));
                  setArrangerOpen(o => !o);
                }}
                data-testid="merge-arrange-all"
              >
                {arranged ? "Arrange all pages (arranged)" : "Arrange all pages"}
              </Button>
            </div>
          )}
          {arrangerOpen && arranged && (
            <MergePageArranger
              files={goodFiles}
              pages={arranged}
              onChange={(next) => { setArranged(next); setResult(null); }}
              onReset={() => { setArranged(arrangementFromPlans(goodFiles.map(f => planFilePages((fileStatuses.get(fileKey(f)) as { pageCount: number }).pageCount, ranges.get(f), edits.get(f))))); setResult(null); }}
              onClose={() => setArrangerOpen(false)}
            />
          )}
          <label className="flex items-center gap-2 text-sm mt-3 max-sm:min-h-11">
            <input type="checkbox" checked={padOdd} disabled={!!arranged} onChange={(e) => { setPadOdd(e.target.checked); setResult(null); }} data-testid="merge-padodd" />
            Add a blank page after any file with an odd number of pages (so each file starts on a front side when printed double-sided){arranged ? " — not used while pages are arranged by hand" : ""}
          </label>
          {result && !processing && (
            <div className="mt-4 pt-4 border-t border-border">
              <PdfPageStrip blob={result} />
            </div>
          )}
          <div className={`flex items-center gap-3 ${result && !processing ? "mt-2" : "mt-4 pt-4 border-t border-border"}`}>
            {processing ? (
              <ProcessingState message="Merging PDFs..." progress={progress} onStop={handleStop} />
            ) : result ? (
              <DownloadButton blob={result} fileName={mergedFileName} label="Download Merged PDF" pinned={{ summary: formatMergedSummary(goodPageCount, result.size) }} />
            ) : (
              <>
                <Button onClick={handleMerge} disabled={goodFiles.length < 2 || rangeErrors > 0} className="btn-glow max-sm:hidden">
                  {mergeLabel}
                </Button>
                {/* Phones: Merge stays pinned at the bottom (above the anchor ad) once files are added. */}
                <StickyActionBar>
                  <div className="min-w-0 flex-1 text-[13px] leading-tight text-muted-foreground" data-testid="merge-bar-summary">
                    {formatSelectionSummary({ fileCount: files.length, pageCount: goodPageCount, pendingCount, unreadableCount: badFileCount }).replace(/ — drag to reorder.*/, "")}
                  </div>
                  <Button onClick={handleMerge} disabled={goodFiles.length < 2 || rangeErrors > 0} className={`${STICKY_BAR_PRIMARY_CLASS} btn-glow`} data-testid="merge-bar-button">
                    {goodFiles.length < 2 ? "Add 2 PDFs" : badFileCount > 0 ? `Merge ${goodFiles.length} PDFs` : mergeLabel}
                  </Button>
                </StickyActionBar>
              </>
            )}
            <Button variant="outline" className="max-sm:min-h-11" onClick={() => { setFiles([]); setRanges(new Map()); setEdits(new Map()); setPickerFile(null); setResult(null); setFileStatuses(new Map()); setThumbnails(new Map()); setFromImage(new Set()); }}>Clear All</Button>
          </div>
        </div>
      )}
    </ToolWithSidebar>
  );
}
